import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms'
import { DateTime } from 'luxon'
import { BX_DATE_REGEX_API, BX_DATE_REGEX_UI, DATE_REGEXP, NAME_REGEXP, SPL_CHAR_REGEXP, WHITESPACE_REGEXP } from './sharedRegex'
import { USER_CONSTANTS_V2 } from '../constants/const'
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'

export function nameValidator (formGroup: UntypedFormGroup) {
  const firstName = formGroup.controls['firstName']
  const lastName = formGroup.controls['lastName']

  const isFirstnameValid = NAME_REGEXP.test(firstName.value)
  const isLastNameValid = NAME_REGEXP.test(lastName.value)

  if (!firstName.value.length) {
    firstName.setErrors({ firstNameRequired: true })
  } else if (firstName.value.length && firstName.value.length < 2) {
    firstName.setErrors({ minlength: { requiredLength: 2 } })
  } else if (firstName.value.length && !isFirstnameValid) {
    firstName.setErrors({ nameInvalid: true, singleHyphen: true })
  } else if (firstName.value.length && firstName.value.length > 50) {
    firstName.setErrors({ maxlength: { requiredLength: 50 } })
  } else {
    firstName.setErrors(null)
  }

  if (!lastName.value.length) {
    lastName.setErrors({lastNameRequired: true})
  } else if (lastName.value.length && lastName.value.length < 1) {
      lastName.setErrors({ minlength: { requiredLength: 1 } })
    } else if (lastName.value.length && !isLastNameValid) {
      lastName.setErrors({ nameInvalid: true, singleHyphen: true })
    } else if (lastName.value.length && lastName.value.length > 50) {
      lastName.setErrors({ maxlength: { requiredLength: 50 } })
    } else {
      lastName.setErrors(null)
    }
  return null
}

export function expiryDateValidator (formGroup: UntypedFormGroup) {
  const expiryDateControl = formGroup.controls['expiryDate']
  const expiryDate = formGroup.controls['expiryDate'].value
  const hasIdImage = formGroup.controls['idImage'].value
  const identityType = formGroup.controls['identityType'].value
  const dateTime = DateTime.fromFormat(expiryDate || '', 'd/M/yyyy')
  let error = null
  if (expiryDate?.length > 0 && expiryDate?.length < 10) {
    return expiryDateControl.setErrors({ dateLengthMatch: true })
  } else {
    if ((!expiryDate && identityType?.option?.code === 'P') ||
      (!expiryDate && identityType === USER_CONSTANTS_V2.identificationTypeId)) {
      error = { required: true }
      expiryDateControl.setErrors(error)
    } else if ((expiryDate && !expiryDateControl.hasError('required')) && !DATE_REGEXP.test(expiryDate)) {
      error = { date: true }
      expiryDateControl.setErrors(error)
    } else if ((expiryDate && !expiryDateControl.hasError('required')) && !(dateTime.isValid && dateTime > DateTime.now().startOf('day'))) {
      error = { invalidExpireDate: true }
      expiryDateControl.setErrors(error)
    } else if ((expiryDate === '31/12/2099') && !hasIdImage) {
      error = { reviewRequired: true }
      expiryDateControl.setErrors(error)
      expiryDateControl.markAsTouched()
    } else {
      expiryDateControl.setErrors(error)
    }
  }
}

export function under16YrsOldValidator (): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) { return null }
    const bdDate = DateTime.fromFormat(control.value || '', 'd/M/yyyy')
    const years = DateTime.now().diff(bdDate, 'years').years
    return years > 0 && years < 16 ? { under16YrsOld: true } : null
  }
}

export function invalidDateValidator (): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value
    if (!value) {
      return null
    }

    let day: number | undefined
    let month: number | undefined
    let year: number | undefined

    if (typeof value === 'string') {
      // DD/MM/YYYY string coming from manual typing
      const parts = value.split('/')
      if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        day = parseInt(parts[0], 10)
        month = parseInt(parts[1], 10)
        year = parseInt(parts[2], 10)
      }
    } else {
      // NgbDateStruct { year, month, day } coming from the calendar popup
      const selectedDate = value as NgbDate
      day = selectedDate?.day
      month = selectedDate?.month
      year = selectedDate?.year
    }

    if (!day || !month || !year) {
      return { invalidDate: true }
    }

    // Reject values that are not real calendar dates (e.g. 31/02/2000, 20/13/1995)
    const inputDate = new Date(year, month - 1, day)
    const isRealDate =
      inputDate.getFullYear() === year &&
      inputDate.getMonth() === month - 1 &&
      inputDate.getDate() === day
    if (!isRealDate) {
      return { invalidDate: true }
    }

    if (inputDate > new Date()) {
      return { futureDate: true }
    }

    return null
  }
}

export function invalidExpiryDateValidator (format = 'MM/dd/yyyy'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }
    let expiryDate: DateTime
    if (typeof control.value === 'object' && control.value.year && control.value.month && control.value.day) {
      const dateStruct = control.value as NgbDateStruct
      expiryDate = DateTime.fromObject({
        year: dateStruct.year,
        month: dateStruct.month,
        day: dateStruct.day
      })
    } else if (typeof control.value === 'string') {
      expiryDate = DateTime.fromFormat(control.value.trim(), format)
    } else {
      return null
    }
    if (!expiryDate.isValid) {
      return null
    }
    const today = DateTime.now()
    const isInvalid = expiryDate.startOf('day') <= today.startOf('day')
    return isInvalid ? { invalidExpireDate: true } : null
  }
}

export function invalidBXDateValidator (format = 'dd/MM/yyyy'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) { return null }
    let bdDate: DateTime
    if (format === 'dd/MM/yyyy') {
      bdDate = DateTime.fromFormat(control.value, format)
    } else if (format === 'yyyy-MM-dd') {
      bdDate = DateTime.fromISO(control.value)
    } else {
      bdDate = DateTime.fromFormat(control.value || '', format)
    }

    const today = DateTime.now()

    if (!bdDate.isValid) {
      return { invalidDate: true }
    }
    return bdDate.startOf('day') > today.startOf('day') ? { futureDate: true } : null
  }
}

export function formatDateOfBirth (uiDateValue: any): string | null {
  if (!uiDateValue) return null

  if (typeof uiDateValue === 'string' && BX_DATE_REGEX_API.test(uiDateValue)) {
    return uiDateValue
  }

  if (typeof uiDateValue === 'string' && BX_DATE_REGEX_UI.test(uiDateValue)) {
    const [day, month, year] = uiDateValue.split('/')
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  if (uiDateValue && typeof uiDateValue === 'object' && uiDateValue.year && uiDateValue.month && uiDateValue.day) {
    const year = uiDateValue.year
    const month = String(uiDateValue.month).padStart(2, '0')
    const day = String(uiDateValue.day).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return null
}

export function namePatternValidator (): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) { return null }

    return !NAME_REGEXP.test(control.value) ? { nameInvalid: true, singleHyphen: true } : null
  }
}

export function noWhitespaceValidator (control: UntypedFormControl) {
  const hasWhiteSpace = (control.value || '').match(WHITESPACE_REGEXP)
  const isValid = !hasWhiteSpace
  return isValid ? null : { noSpace: true }
}

export function noSpecialCharactersValidator (control: UntypedFormControl) {
  const hasSplChars = (control.value || '').match(SPL_CHAR_REGEXP)
  const isValid = !hasSplChars
  return isValid ? null : { noSpace: true }
}

export function addressDefaultValueValidator (control: UntypedFormControl) {
  const addressValue = control.value
  const isValid = (addressValue !== USER_CONSTANTS_V2.streetAddress1)
  if (!isValid) {
    control.markAsTouched()
  }
  return isValid ? null : { reviewRequired: true }
}

export function countryDefaultValueValidator (control: UntypedFormControl) {
  const countryValue = control.value
  let isValid
  if (typeof countryValue === 'object' && 'Id' in countryValue) {
    isValid = countryValue.Id !== USER_CONSTANTS_V2.countryId
  } else {
    isValid = countryValue !== USER_CONSTANTS_V2.countryId
  }
  if (!isValid) {
    control.markAsTouched()
  }
  return isValid ? null : { reviewRequired: true }
}

export function nationalityDefaultValueValidator (control: UntypedFormControl) {
  const nationalityValue = control.value
  const isValid = (nationalityValue !== USER_CONSTANTS_V2.nationalityId)
  if (!isValid) {
    control.markAsTouched()
  }
  return isValid ? null : { reviewRequired: true }
}

export function dateLengthValidator (formGroup: UntypedFormGroup) {
  const birthDate = formGroup?.controls['birthDate']
  if (birthDate?.value?.length > 0 && birthDate?.value?.length < 10) {
    return birthDate.setErrors({ dateLengthMatch: true })
  }
}
export function birthdateLengthValidator (control: UntypedFormControl) {
  const birthDateControl = control.value
  return (birthDateControl?.length > 0 && birthDateControl?.length < 10) ? { dateLengthMatch: true } : null
}
